/**
 * SpriteResolver — lazily loads + caches a species' sprites by national-dex number,
 * applying the ingest's fallback chain (animated -> static -> official -> placeholder
 * for battle; emotion -> Normal -> battle -> placeholder for portraits) and NEVER
 * throwing on a miss. Resolved Phaser textures are keyed content-stably so a busy
 * town reuses one decoded texture per species (sprite-POOLING).
 *
 * Loading is split into a pure fallback driver (`resolveFirst`) + a Phaser loader
 * adapter, so the SELECTION logic is unit-testable with no canvas/DOM (see tests).
 */
import Phaser from 'phaser';
import type { Emotion } from '@/shared';
import type { RenderSpriteMeta, RenderSpriteSide } from '@/data';
import {
  animDataUrl,
  battleSpriteCandidates,
  overworldIdleUrl,
  overworldWalkUrl,
  portraitCandidates,
  renderAnimKey,
  renderUrl,
  textureKey,
  tierOf,
  PMD_DIRECTION_ORDER,
  type Direction,
  type Facing,
} from './paths.js';
import { resolveFirst, type ResolvedSprite } from './resolve.js';
import {
  parseAnimData,
  columnsForSheet,
  rowsForSheet,
  standingFrameIndex,
  frameIndex,
  type AnimGeometry,
} from './animData.js';

/**
 * What a scene needs to spawn an animated, directional overworld sprite. Two shapes
 * behind one return:
 *  - `kind: 'sheet'`  — a real PMD spritesheet: add `Phaser.GameObjects.Sprite`s on
 *    `walkTextureKey`, play `walkAnimKey(dir)` to walk, `idleAnimKey(dir)` to idle,
 *    or just `setFrame(standingFrame(dir))` to stand. Defaults face south, frame 0.
 *  - `kind: 'static'` — every PMD load missed; fall back to the species' battle FRONT
 *    sprite as a plain (non-directional) image on `textureKey`.
 * Either way the scene can render *something* facing the player — the method never
 * throws and never returns null.
 */
export type OverworldSprite =
  | {
      readonly kind: 'sheet';
      /** Texture key holding the sliced walk spritesheet (add sprites on this). */
      readonly walkTextureKey: string;
      /** Texture key holding the sliced idle spritesheet (may equal the walk key). */
      readonly idleTextureKey: string;
      /** Per-frame width/height the sheet was sliced at (for scaling to the chibis). */
      readonly frameWidth: number;
      readonly frameHeight: number;
      /** Columns (animation frames) + rows (facing directions) of the walk sheet. */
      readonly columns: number;
      readonly rows: number;
      /** The default standing pose: south-facing, frame 0. */
      readonly defaultFacing: Direction;
      readonly defaultFrame: number;
      /** Phaser animation key to WALK facing `dir` (registered globally, idempotent). */
      walkAnimKey(dir: Direction): string;
      /** Phaser animation key to IDLE facing `dir`. */
      idleAnimKey(dir: Direction): string;
      /** The static (standing) frame index on the walk sheet for `dir`. */
      standingFrame(dir: Direction): number;
    }
  | {
      readonly kind: 'static';
      /** Battle-front texture key to render as a plain image (no directions). */
      readonly textureKey: string;
      readonly defaultFacing: Direction;
    };

/**
 * A resolved 3D-model RENDER spritesheet — a vertical sheet (frames stacked
 * top->bottom) sliced into `frameCount` cells of `frameWidth` x `frameHeight`, with
 * a gentle looping idle animation already registered globally under `animKey`. The
 * scene adds a `Phaser.GameObjects.Sprite` on `textureKey` and `play(animKey)` to
 * amble, or `setFrame(0)` for a static first-frame (dex grid). Per-side: front/back
 * resolve independently, each falling back to the Gen5 battle chain when absent.
 */
export interface RenderSprite {
  /** Texture key holding the sliced vertical render sheet. */
  readonly textureKey: string;
  /** Global animation key for the gentle looping idle (registered, idempotent). */
  readonly animKey: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly frameCount: number;
}

export class SpriteResolver {
  /** Resolved keys -> winning url, so repeat requests are O(1) (the pool). */
  private readonly resolved = new Map<string, ResolvedSprite>();
  /** In-flight resolutions, so concurrent callers for the same key share one load. */
  private readonly inflight = new Map<string, Promise<ResolvedSprite | null>>();
  /** Built overworld spritesheets by dex (a busy town slices each sheet once). */
  private readonly overworldPool = new Map<number, OverworldSprite>();
  /** In-flight overworld builds, so concurrent callers share one slice. */
  private readonly overworldInflight = new Map<number, Promise<OverworldSprite>>();
  /** Built render spritesheets by texture key (slice each sheet exactly once). */
  private readonly renderPool = new Map<string, RenderSprite | null>();
  /** In-flight render builds, so concurrent callers share one slice. */
  private readonly renderInflight = new Map<string, Promise<RenderSprite | null>>();
  /** Per-dex render metadata (frame dims) the ingest recorded; empty until set. */
  private renderIndex: Readonly<Record<number, RenderSpriteMeta>> = {};

  constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Install the 3D-render manifest (from the bundled dataset's `renders` field, or a
   * standalone renders.json). Without it, `renderBattle()` has no frame dimensions to
   * slice and quietly returns null, so battles/dex fall back to the Gen5 chain — never
   * a crash. Idempotent; the shared resolver is set up once in PreloadScene.
   */
  setRenderIndex(index: Readonly<Record<number, RenderSpriteMeta>> | undefined | null): void {
    this.renderIndex = index ?? {};
  }

  /** Whether a 3D render side exists for this species (front/back), per the manifest. */
  hasRender(dexNo: number, facing: Facing = 'front'): boolean {
    return !!this.renderSideFor(dexNo, facing);
  }

  private renderSideFor(dexNo: number, facing: Facing): RenderSpriteSide | undefined {
    const meta = this.renderIndex[dexNo];
    if (!meta) return undefined;
    return facing === 'back' ? meta.back : meta.front;
  }

  /** Already-resolved entry for a key, if cached (no load triggered). */
  peek(key: string): ResolvedSprite | undefined {
    return this.resolved.get(key);
  }

  /** Number of distinct textures resolved so far (pool size; for diagnostics). */
  get size(): number {
    return this.resolved.size;
  }

  /** Resolve a battle sprite (front/back) with the full fallback chain. */
  battle(dexNo: number, facing: Facing = 'front'): Promise<ResolvedSprite | null> {
    return this.resolve(textureKey('battle', dexNo, facing), battleSpriteCandidates(dexNo, facing));
  }

  /** Resolve an emotion portrait (chat layer) with the full fallback chain. */
  portrait(dexNo: number, emotion: Emotion = 'normal'): Promise<ResolvedSprite | null> {
    return this.resolve(textureKey('portrait', dexNo, emotion), portraitCandidates(dexNo, emotion));
  }

  /** Resolve the 8-direction overworld WALK sheet (falls back to battle front). */
  walk(dexNo: number): Promise<ResolvedSprite | null> {
    const candidates = [overworldWalkUrl(dexNo), ...battleSpriteCandidates(dexNo, 'front')];
    return this.resolve(textureKey('walk', dexNo), candidates);
  }

  /** Resolve the overworld IDLE sheet (falls back to the walk sheet, then battle). */
  idle(dexNo: number): Promise<ResolvedSprite | null> {
    const candidates = [
      overworldIdleUrl(dexNo),
      overworldWalkUrl(dexNo),
      ...battleSpriteCandidates(dexNo, 'front'),
    ];
    return this.resolve(textureKey('idle', dexNo), candidates);
  }

  /**
   * Resolve a species' 3D-model RENDER spritesheet for a facing (the PREFERRED battle
   * + dex sprite). Loads the vertical sheet recorded in the render manifest, slices it
   * into `frameCount` cells via `textures.addSpriteSheet`, and registers a gentle
   * looping idle animation — returning everything a scene needs to amble it (or hold
   * its first frame). Pooled by texture key (slice each side once).
   *
   * Returns null (NEVER throws) when this species/side has no render in the manifest,
   * when the manifest was never installed, or on any decode failure — the caller then
   * falls back to {@link battle} (Gen5 animated -> static -> official -> placeholder).
   */
  renderBattle(dexNo: number, facing: Facing = 'front'): Promise<RenderSprite | null> {
    const side = this.renderSideFor(dexNo, facing);
    if (!side) return Promise.resolve(null);
    const key = textureKey('render', dexNo, facing);

    if (this.renderPool.has(key)) return Promise.resolve(this.renderPool.get(key) ?? null);
    const pending = this.renderInflight.get(key);
    if (pending) return pending;

    const job = this.buildRender(key, dexNo, facing, side).then((built) => {
      this.renderInflight.delete(key);
      this.renderPool.set(key, built);
      return built;
    });
    this.renderInflight.set(key, job);
    return job;
  }

  /** Internal: load + slice one render side into a sheet + idle anim. Never throws. */
  private async buildRender(
    key: string,
    dexNo: number,
    facing: Facing,
    side: RenderSpriteSide,
  ): Promise<RenderSprite | null> {
    try {
      const url = side.path || renderUrl(dexNo, facing);
      const loaded = await this.loadVerticalSheet(key, url, side);
      if (!loaded) return null;
      const animKey = renderAnimKey(key);
      this.registerRenderIdle(key, animKey, loaded.frameCount);
      return {
        textureKey: key,
        animKey,
        frameWidth: side.frameWidth,
        frameHeight: side.frameHeight,
        frameCount: loaded.frameCount,
      };
    } catch {
      return null;
    }
  }

  /**
   * Load a VERTICAL render PNG (frames stacked top->bottom) into a sliced spritesheet
   * texture `key`. The manifest records the per-frame dimensions; we derive the true
   * frame count from the decoded image height so a mismatch never over/under-slices.
   * Loaded via a plain Image (race-free, same as tryLoad). Resolves the frame count or
   * null on any failure. Never throws.
   */
  private loadVerticalSheet(
    key: string,
    url: string,
    side: RenderSpriteSide,
  ): Promise<{ frameCount: number } | null> {
    if (this.scene.textures.exists(key)) {
      const src = this.scene.textures.get(key).getSourceImage() as { height?: number };
      const h = src?.height ?? side.frameHeight * side.frameCount;
      return Promise.resolve({ frameCount: frameCountFor(h, side.frameHeight, side.frameCount) });
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = (): void => {
        try {
          const frameCount = frameCountFor(img.height, side.frameHeight, side.frameCount);
          if (!this.scene.textures.exists(key)) {
            this.scene.textures.addSpriteSheet(key, img, {
              frameWidth: side.frameWidth,
              frameHeight: side.frameHeight,
            });
          }
          resolve(this.scene.textures.exists(key) ? { frameCount } : null);
        } catch {
          resolve(null);
        }
      };
      img.onerror = (): void => resolve(null);
      img.src = url;
    });
  }

  /**
   * Register a gentle, calm looping idle that plays the render's full frame run.
   * Idempotent (skips an already-registered key) so re-resolving a pooled species is
   * free. The slow frame rate keeps battles/dex unhurried (CLAUDE.md: gentle).
   */
  private registerRenderIdle(sheetKey: string, animKey: string, frameCount: number): void {
    if (this.scene.anims.exists(animKey)) return;
    const last = Math.max(0, frameCount - 1);
    this.scene.anims.create({
      key: animKey,
      frames: this.scene.anims.generateFrameNumbers(sheetKey, { start: 0, end: last }),
      frameRate: RENDER_IDLE_FPS,
      repeat: -1,
    });
  }

  /**
   * Load a species' PMD overworld sprite as a proper, SLICED Phaser spritesheet and
   * return everything a scene needs to spawn an animated directional sprite (default:
   * south-facing, frame 0 standing). Fetches the species' AnimData.xml, parses the
   * Walk + Idle FrameWidth/Height, slices Walk-Anim.png / Idle-Anim.png into per-cell
   * frames via `textures.addSpriteSheet`, and registers per-direction `walk-<dir>` /
   * `idle-<dir>` animations from each direction's row of frames.
   *
   * NEVER throws: any miss (no AnimData, a 404 PNG, an un-decodable image, a Walk
   * that is only a <CopyOf>) falls back to the species' battle FRONT sprite as a
   * static image (`kind: 'static'`). Results are pooled by dex number, so a town
   * full of one species slices the sheet exactly once.
   */
  async overworldSprite(
    dexNo: number,
    fetchImpl: typeof fetch = (typeof fetch === 'function' ? fetch : undefined) as typeof fetch,
  ): Promise<OverworldSprite> {
    const cached = this.overworldPool.get(dexNo);
    if (cached) return cached;
    const pending = this.overworldInflight.get(dexNo);
    if (pending) return pending;

    const job = this.buildOverworldSprite(dexNo, fetchImpl).then((built) => {
      this.overworldInflight.delete(dexNo);
      this.overworldPool.set(dexNo, built);
      return built;
    });
    this.overworldInflight.set(dexNo, job);
    return job;
  }

  /** Internal: do the actual fetch + slice + animation registration. Never throws. */
  private async buildOverworldSprite(
    dexNo: number,
    fetchImpl: typeof fetch,
  ): Promise<OverworldSprite> {
    try {
      const xml = await this.fetchText(animDataUrl(dexNo), fetchImpl);
      const anim = xml ? parseAnimData(xml) : { walk: null, idle: null };

      // The walk sheet is the spine; without it (no AnimData or no Walk geometry) we
      // can't slice directional frames — fall back to the static battle sprite. NOTE
      // the 'walksheet'/'idlesheet' keys are DISTINCT from the legacy walk()/idle()
      // plain-image keys, so the two paths never collide on one texture.
      if (!anim.walk) return this.overworldFallback(dexNo);

      const walkKey = textureKey('walksheet', dexNo);
      const walk = await this.loadSheet(walkKey, overworldWalkUrl(dexNo), anim.walk);
      if (!walk) return this.overworldFallback(dexNo);

      // Idle is optional: reuse the walk sheet's standing frames when it is absent.
      const idleKey = textureKey('idlesheet', dexNo);
      const idle = anim.idle
        ? await this.loadSheet(idleKey, overworldIdleUrl(dexNo), anim.idle)
        : null;

      this.registerDirectionAnims(walkKey, 'walk', anim.walk, walk.columns, walk.rows);
      if (idle) {
        this.registerDirectionAnims(
          idleKey,
          'idle',
          anim.idle as AnimGeometry,
          idle.columns,
          idle.rows,
        );
      } else {
        // No idle sheet ⇒ idle anims read the walk sheet's frame 0 per direction.
        this.registerStandingAnims(walkKey, 'idle', anim.walk, walk.columns);
      }

      const idleTextureKey = idle ? idleKey : walkKey;
      const cols = walk.columns;
      const rows = walk.rows;
      return {
        kind: 'sheet',
        walkTextureKey: walkKey,
        idleTextureKey,
        frameWidth: anim.walk.frameWidth,
        frameHeight: anim.walk.frameHeight,
        columns: cols,
        rows,
        defaultFacing: 'south',
        defaultFrame: standingFrameIndex('south', cols),
        walkAnimKey: (dir: Direction) => animKey(walkKey, 'walk', dir),
        idleAnimKey: (dir: Direction) => animKey(idleTextureKey, 'idle', dir),
        standingFrame: (dir: Direction) => standingFrameIndex(dir, cols),
      };
    } catch {
      return this.overworldFallback(dexNo);
    }
  }

  /** The static (battle-front) fallback for a species with no usable PMD sheet. */
  private async overworldFallback(dexNo: number): Promise<OverworldSprite> {
    const resolved = await this.battle(dexNo, 'front').catch(() => null);
    return {
      kind: 'static',
      textureKey: resolved?.key ?? textureKey('battle', dexNo, 'front'),
      defaultFacing: 'south',
    };
  }

  /**
   * Load one PMD anim PNG into a SLICED spritesheet texture `key` (frameWidth x
   * frameHeight cells). Resolves the sheet's column/row counts (from the decoded
   * image dimensions) or null on any failure. Loaded via a plain Image so concurrent
   * resolves never race the scene loader. Never throws.
   */
  private loadSheet(
    key: string,
    url: string,
    geom: AnimGeometry,
  ): Promise<{ columns: number; rows: number } | null> {
    // Already sliced under this key (pooled from a prior overworld build): derive the
    // grid from the cached source image so we never re-decode.
    if (this.scene.textures.exists(key)) {
      const src = this.scene.textures.get(key).getSourceImage() as {
        width?: number;
        height?: number;
      };
      const w = src?.width ?? geom.frameWidth;
      const h = src?.height ?? geom.frameHeight;
      return Promise.resolve({
        columns: columnsForSheet(w, geom.frameWidth),
        rows: rowsForSheet(h, geom.frameHeight),
      });
    }
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = (): void => {
        try {
          const columns = columnsForSheet(img.width, geom.frameWidth);
          const rows = rowsForSheet(img.height, geom.frameHeight);
          if (!this.scene.textures.exists(key)) {
            this.scene.textures.addSpriteSheet(key, img, {
              frameWidth: geom.frameWidth,
              frameHeight: geom.frameHeight,
            });
          }
          resolve(this.scene.textures.exists(key) ? { columns, rows } : null);
        } catch {
          resolve(null);
        }
      };
      img.onerror = (): void => resolve(null);
      img.src = url;
    });
  }

  /**
   * Register a `walk-<dir>` / `idle-<dir>` global Phaser animation per facing, each
   * playing that direction's full row of frames. Idempotent (skips keys already
   * registered) so re-resolving a pooled species is free.
   */
  private registerDirectionAnims(
    sheetKey: string,
    kind: 'walk' | 'idle',
    geom: AnimGeometry,
    columns: number,
    rows: number,
  ): void {
    for (const dir of PMD_DIRECTION_ORDER) {
      const key = animKey(sheetKey, kind, dir);
      if (this.scene.anims.exists(key)) continue;
      const row = directionRowFor(dir);
      if (row >= rows) continue; // partial sheet missing this direction's row
      const start = frameIndex(dir, columns, 0);
      const frames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let f = 0; f < columns; f++) frames.push({ key: sheetKey, frame: start + f });
      this.scene.anims.create({
        key,
        frames,
        frameRate: frameRateFor(geom),
        repeat: -1,
      });
    }
  }

  /**
   * Register single-frame `idle-<dir>` animations that just hold a direction's
   * standing (frame-0) cell — used when a species has no dedicated Idle sheet.
   */
  private registerStandingAnims(
    sheetKey: string,
    kind: 'idle',
    geom: AnimGeometry,
    columns: number,
  ): void {
    for (const dir of PMD_DIRECTION_ORDER) {
      const key = animKey(sheetKey, kind, dir);
      if (this.scene.anims.exists(key)) continue;
      this.scene.anims.create({
        key,
        frames: [{ key: sheetKey, frame: standingFrameIndex(dir, columns) }],
        frameRate: frameRateFor(geom),
        repeat: -1,
      });
    }
  }

  /** Fetch a text resource (AnimData.xml). Returns null on any failure; never throws. */
  private async fetchText(url: string, fetchImpl: typeof fetch): Promise<string | null> {
    if (typeof fetchImpl !== 'function') return null;
    try {
      const res = await fetchImpl(url);
      if (!res.ok) return null;
      return await res.text();
    } catch {
      return null;
    }
  }

  /** Drop the in-memory pool (keeps Phaser textures; for HMR/test isolation). */
  clearCache(): void {
    this.resolved.clear();
    this.inflight.clear();
    this.overworldPool.clear();
    this.overworldInflight.clear();
    this.renderPool.clear();
    this.renderInflight.clear();
  }

  /** Core: cached -> in-flight -> walk the fallback chain via the Phaser loader. */
  private resolve(key: string, candidates: readonly string[]): Promise<ResolvedSprite | null> {
    const hit = this.resolved.get(key);
    if (hit) return Promise.resolve(hit);
    const pending = this.inflight.get(key);
    if (pending) return pending;

    const job = resolveFirst(key, candidates, (k, url) => this.tryLoad(k, url)).then((url) => {
      this.inflight.delete(key);
      if (url == null) return null;
      const entry: ResolvedSprite = { key, url, tier: tierOf(url) };
      this.resolved.set(key, entry);
      return entry;
    });
    this.inflight.set(key, job);
    return job;
  }

  /**
   * Attempt to load one url into a texture `key`. Loads via a plain HTMLImageElement
   * + `textures.addImage` rather than the scene LoaderPlugin — so concurrent resolves
   * (e.g. three starter cards, or a town full of trainers all at once) never race on a
   * shared loader / overlapping `loader.start()` calls. Resolves `true` only if the
   * texture decoded; a 404 / decode error resolves `false` so the fallback chain
   * advances. Animated GIFs decode to their first frame. Never throws.
   */
  private tryLoad(key: string, url: string): Promise<boolean> {
    // Already in Phaser's texture cache under this key (pooled from a prior load).
    if (this.scene.textures.exists(key)) return Promise.resolve(true);

    return new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = (): void => {
        try {
          if (!this.scene.textures.exists(key)) this.scene.textures.addImage(key, img);
          resolve(this.scene.textures.exists(key));
        } catch {
          resolve(false);
        }
      };
      img.onerror = (): void => resolve(false);
      img.src = url;
    });
  }
}

// ── module helpers (pure; kept out of the class for clarity) ───────────────────

/**
 * Calm playback rate (fps) for a 3D-render idle — a soft amble, not a race. Renders
 * are many short frames; a slow, fixed rate keeps battles + the dex unhurried.
 */
const RENDER_IDLE_FPS = 8;

/**
 * Frames in a vertical render sheet from its decoded height. Prefers the integer
 * count the height implies (so a sheet that decoded to a different size than the
 * manifest recorded never over/under-slices), clamped to at least 1 and falling back
 * to the manifest's count if the height is unusable.
 */
function frameCountFor(sheetHeight: number, frameHeight: number, manifestCount: number): number {
  if (frameHeight > 0 && sheetHeight > 0) {
    const derived = Math.floor(sheetHeight / frameHeight);
    if (derived >= 1) return derived;
  }
  return Math.max(1, manifestCount);
}

/** Stable global animation key: `<sheetKey>:<walk|idle>:<dir>`. */
function animKey(sheetKey: string, kind: 'walk' | 'idle', dir: Direction): string {
  return `${sheetKey}:${kind}:${dir}`;
}

/** Zero-based PMD row for a facing (south = 0). Mirrors paths.directionRow. */
function directionRowFor(dir: Direction): number {
  const i = PMD_DIRECTION_ORDER.indexOf(dir);
  return i < 0 ? 0 : i;
}

/**
 * A gentle, calm playback rate (fps) for a PMD anim. PMD durations are in 60fps
 * ticks; we derive an average-tick frame rate, clamped soft so the populated world
 * ambles rather than races (CLAUDE.md: unhurried, gentle animations).
 */
function frameRateFor(geom: AnimGeometry): number {
  const ticks = geom.durations.length > 0 ? geom.durations : [8];
  const avg = ticks.reduce((a, b) => a + b, 0) / ticks.length;
  const fps = avg > 0 ? 60 / avg : 8;
  return Math.max(2, Math.min(10, fps));
}
